import {
  SettingsIcon,
  XIcon,
  HelpCircleIcon,
  RotateCcwIcon,
  BookOpenIcon,
  UserIcon,
  SunIcon,
  MoonIcon,
  ClockIcon,
  ChevronDownIcon,
  CheckIcon,
  Globe,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useCallback, useContext, useState, useEffect } from "react";
import { usePanelVisibility } from "@/state/state-storage";
import { useTradingSettings } from "@/features/trading-settings/trading-settings-context";
import { OnboardingContext } from "@/features/onboarding/onboarding-context";
import { useTheme } from "@/theme/theme-context";
import { useI18n } from "@/features/i18n";
import {
  COMMON_TIMEZONES,
  SUPPORTED_LOCALES,
  LOCALE_NAMES,
  LOCALE_FLAGS,
  type TimezoneOption,
} from "@nordex/i18n";

// Middle East timezone values
const MIDDLE_EAST_TZ = ['Asia/Dubai', 'Asia/Riyadh'];

// Group timezones by region for better UX
const TIMEZONE_GROUPS: { label: string; timezones: TimezoneOption[] }[] = [
  {
    label: "Recommended",
    timezones: COMMON_TIMEZONES.filter(tz => tz.recommended),
  },
  {
    label: "Americas",
    timezones: COMMON_TIMEZONES.filter(tz => tz.value.startsWith("America/")),
  },
  {
    label: "Europe",
    timezones: COMMON_TIMEZONES.filter(tz => tz.value.startsWith("Europe/")),
  },
  {
    label: "Middle East",
    timezones: COMMON_TIMEZONES.filter(tz => MIDDLE_EAST_TZ.includes(tz.value)),
  },
  {
    label: "Africa",
    timezones: COMMON_TIMEZONES.filter(tz => tz.value.startsWith("Africa/")),
  },
  {
    label: "Asia",
    timezones: COMMON_TIMEZONES.filter(tz => 
      tz.value.startsWith("Asia/") && !MIDDLE_EAST_TZ.includes(tz.value)
    ),
  },
  {
    label: "Oceania",
    timezones: COMMON_TIMEZONES.filter(tz => 
      tz.value.startsWith("Australia/") || tz.value.startsWith("Pacific/")
    ),
  },
];

type PanelVisibilityState = {
  orderBookVisible: boolean;
  accountVisible: boolean;
};

type SettingsButtonProps = {
  className?: string;
  onResetLayout?: () => void;
  onTogglePanel?: (visibility: PanelVisibilityState) => void;
  showLayoutControls?: boolean; // Show panel toggles and reset layout (only on trading page)
};

export const SettingsButton = ({ 
  className, 
  onResetLayout,
  onTogglePanel,
  showLayoutControls = false,
}: SettingsButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [panelVisibility, setPanelVisibility] = usePanelVisibility();
  const { slippageTolerance, setSlippageTolerance, defaultLeverage, setDefaultLeverage } = useTradingSettings();
  const onboardingContext = useContext(OnboardingContext);
  const { theme, updateTheme } = useTheme();
  
  // I18n context for timezone, language, and clock format
  const { timezone, language, clockFormat, updateTimezone, updateLanguage, updateClockFormat, formatTradingTime: formatTime } = useI18n();
  
  // Regional section state
  const [isTimezoneDropdownOpen, setIsTimezoneDropdownOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  const toggleClockFormat = useCallback(() => {
    const newFormat = clockFormat === '12h' ? '24h' : '12h';
    updateClockFormat(newFormat);
  }, [clockFormat, updateClockFormat]);
  
  // Update current time every second for live preview
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Get current timezone label
  const currentTimezoneLabel = COMMON_TIMEZONES.find(tz => tz.value === timezone)?.label || timezone;

  const toggleOrderBook = useCallback(() => {
    const newVisibility = {
      orderBookVisible: !panelVisibility.orderBookVisible,
      accountVisible: panelVisibility.accountVisible,
    };
    setPanelVisibility(newVisibility);
    onTogglePanel?.(newVisibility);
  }, [panelVisibility, setPanelVisibility, onTogglePanel]);

  const toggleAccount = useCallback(() => {
    const newVisibility = {
      orderBookVisible: panelVisibility.orderBookVisible,
      accountVisible: !panelVisibility.accountVisible,
    };
    setPanelVisibility(newVisibility);
    onTogglePanel?.(newVisibility);
  }, [panelVisibility, setPanelVisibility, onTogglePanel]);

  const handleReset = useCallback(() => {
    onResetLayout?.();
    setPanelVisibility({
      orderBookVisible: true,
      accountVisible: true,
    });
    setIsResetDialogOpen(false);
  }, [onResetLayout, setPanelVisibility]);

  return (
    <>
      <Button
        variant="outline"
        size="icon"
        onClick={() => setIsOpen(true)}
        className={cn("size-9", className)}
        title="Settings"
      >
        <SettingsIcon className="size-4" />
      </Button>

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Settings</SheetTitle>
            <SheetClose asChild>
              <button className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                <XIcon className="size-4" />
                <span className="sr-only">Close</span>
              </button>
            </SheetClose>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto">
            {/* Trading Section */}
            <div className="px-4 py-4">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                Trading
              </h3>
              
              <div className="space-y-3">
                {/* Default Leverage */}
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <label className="text-sm">Default Leverage</label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircleIcon className="size-3.5 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[200px]">
                        Applies to new markets without a custom leverage setting
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex gap-1.5">
                    {[1, 5, 10, 25, 50].map((lev) => (
                      <button
                        key={lev}
                        onClick={() => setDefaultLeverage(lev)}
                        className={cn(
                          "flex-1 py-1.5 px-2 text-xs font-medium rounded border transition-colors",
                          defaultLeverage === lev
                            ? "border-buy bg-buy/10 text-buy"
                            : "border-border bg-muted/30 text-muted-foreground hover:border-muted-foreground hover:text-foreground"
                        )}
                      >
                        {lev}x
                      </button>
                    ))}
                  </div>
                </div>

                {/* Slippage Tolerance */}
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <label className="text-sm">Slippage Tolerance (Market Orders)</label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircleIcon className="size-3.5 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[220px]">
                        Market orders reject if price deviates more than this from oracle
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => setSlippageTolerance(null)}
                      className={cn(
                        "flex-1 py-1.5 px-2 text-xs font-medium rounded border transition-colors",
                        slippageTolerance === null
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border bg-muted/30 text-muted-foreground hover:border-muted-foreground hover:text-foreground"
                      )}
                    >
                      Auto
                    </button>
                    {[0.1, 0.25, 0.5, 1.0].map((pct) => (
                      <button
                        key={pct}
                        onClick={() => setSlippageTolerance(pct / 100)}
                        className={cn(
                          "flex-1 py-1.5 px-2 text-xs font-medium rounded border transition-colors",
                          slippageTolerance === pct / 100
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border bg-muted/30 text-muted-foreground hover:border-muted-foreground hover:text-foreground"
                        )}
                      >
                        {pct}%
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t" />

            {/* Appearance Section - includes theme and panel toggles */}
            <div className="px-4 py-4">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                Appearance
              </h3>
              <div className="space-y-1">
                {/* Dark Mode Toggle */}
                <div className="flex items-center justify-between py-2.5 px-3 rounded-md hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    {theme === "dark" ? (
                      <MoonIcon className="size-4 text-muted-foreground" />
                    ) : (
                      <SunIcon className="size-4 text-muted-foreground" />
                    )}
                    <span className="text-sm">Dark Mode</span>
                  </div>
                  <Switch
                    checked={theme === "dark"}
                    onCheckedChange={(checked) => updateTheme(checked ? "dark" : "light")}
                  />
                </div>
                
                {/* Panel toggles - only show on trading pages */}
                {showLayoutControls && (
                  <>
                    <div className="flex items-center justify-between py-2.5 px-3 rounded-md hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <BookOpenIcon className="size-4 text-muted-foreground" />
                        <span className="text-sm">Order Book</span>
                      </div>
                      <Switch
                        checked={panelVisibility.orderBookVisible}
                        onCheckedChange={toggleOrderBook}
                      />
                    </div>
                    <div className="flex items-center justify-between py-2.5 px-3 rounded-md hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <UserIcon className="size-4 text-muted-foreground" />
                        <span className="text-sm">Account</span>
                      </div>
                      <Switch
                        checked={panelVisibility.accountVisible}
                        onCheckedChange={toggleAccount}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="border-t" />

            {/* Regional Section - Language and Timezone */}
            <div className="px-4 py-4">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                Regional
              </h3>
              <div className="space-y-3">
                {/* Language Selector */}
                <div className="relative">
                    <button
                      onClick={() => {
                        setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
                        setIsTimezoneDropdownOpen(false);
                      }}
                      className="w-full flex items-center justify-between py-2 px-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-base">{LOCALE_FLAGS[language]}</span>
                        <span className="text-sm">{LOCALE_NAMES[language]}</span>
                      </div>
                      <ChevronDownIcon className={cn(
                        "size-4 text-muted-foreground transition-transform",
                        isLanguageDropdownOpen && "rotate-180"
                      )} />
                    </button>
                    
                    {isLanguageDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 py-1 bg-popover border border-border rounded-lg shadow-lg">
                        {SUPPORTED_LOCALES.map((locale) => (
                          <button
                            key={locale}
                            onClick={() => {
                              updateLanguage(locale);
                              setIsLanguageDropdownOpen(false);
                            }}
                            className={cn(
                              "w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-muted/50 transition-colors",
                              language === locale && "bg-muted/30"
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-base">{LOCALE_FLAGS[locale]}</span>
                              <span>{LOCALE_NAMES[locale]}</span>
                            </div>
                            {language === locale && (
                              <CheckIcon className="size-4 text-primary" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                {/* Timezone Selector */}
                <div className="relative">
                    <button
                      onClick={() => {
                        setIsTimezoneDropdownOpen(!isTimezoneDropdownOpen);
                        setIsLanguageDropdownOpen(false);
                      }}
                      className="w-full flex items-center justify-between py-2 px-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2">
                        <Globe className="size-4 text-muted-foreground" />
                        <span className="text-sm truncate">{currentTimezoneLabel}</span>
                      </div>
                      <ChevronDownIcon className={cn(
                        "size-4 text-muted-foreground transition-transform",
                        isTimezoneDropdownOpen && "rotate-180"
                      )} />
                    </button>
                    
                    {isTimezoneDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 py-1 bg-popover border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                        {TIMEZONE_GROUPS.map((group) => (
                          group.timezones.length > 0 && (
                            <div key={group.label}>
                              <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider bg-muted/30">
                                {group.label}
                              </div>
                              {group.timezones.map((tz) => (
                                <button
                                  key={tz.value}
                                  onClick={() => {
                                    updateTimezone(tz.value);
                                    setIsTimezoneDropdownOpen(false);
                                  }}
                                  className={cn(
                                    "w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-muted/50 transition-colors",
                                    timezone === tz.value && "bg-muted/30"
                                  )}
                                >
                                  <span className="truncate">{tz.label}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">{tz.offset}</span>
                                    {timezone === tz.value && (
                                      <CheckIcon className="size-4 text-primary" />
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )
                        ))}
                      </div>
                    )}
                </div>

                {/* Clock Format Toggle */}
                <div className="flex items-center justify-between py-2 px-3 rounded-lg border border-border bg-muted/30">
                  <div className="flex items-center gap-2">
                    <ClockIcon className="size-4 text-muted-foreground" />
                    <span className="text-sm">Time format</span>
                  </div>
                  <button
                    onClick={toggleClockFormat}
                    className="flex items-center gap-0.5 px-1 py-0.5 rounded-md bg-muted hover:bg-muted/80 active:scale-95 transition-all duration-75 cursor-pointer"
                  >
                    <span className={cn(
                      "px-2 py-0.5 text-xs font-medium rounded transition-all duration-75",
                      clockFormat === '12h' ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                    )}>
                      12h
                    </span>
                    <span className={cn(
                      "px-2 py-0.5 text-xs font-medium rounded transition-all duration-75",
                      clockFormat === '24h' ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                    )}>
                      24h
                    </span>
                  </button>
                </div>
                  
                {/* Live time preview */}
                <div className="px-3 py-1.5 rounded-lg bg-muted/30 text-center">
                  <span className="text-xs text-muted-foreground">Current time: </span>
                  <span className="text-sm font-medium">
                    {formatTime(currentTime)}
                  </span>
                </div>
              </div>
            </div>

            <div className="border-t" />

            {/* Help Section - includes Platform Guide and Reset to Default */}
            <div className="px-4 py-4">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                Help
              </h3>
              <div className="space-y-1">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    onboardingContext?.restartTour();
                  }}
                  className={cn(
                    "w-full flex items-center justify-between py-2.5 px-3 rounded-md hover:bg-muted/50 transition-colors text-left",
                    !onboardingContext && "opacity-50 cursor-not-allowed"
                  )}
                  disabled={!onboardingContext}
                >
                  <div className="flex items-center gap-3">
                    <HelpCircleIcon className="size-4 text-muted-foreground" />
                    <span className="text-sm">Platform Guide</span>
                  </div>
                  <span className="text-muted-foreground text-xs">→</span>
                </button>

                {/* Reset to Default - only show on trading pages */}
                {showLayoutControls && (
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      setIsResetDialogOpen(true);
                    }}
                    className="w-full flex items-center gap-3 py-2.5 px-3 rounded-md hover:bg-muted/50 transition-colors text-left text-muted-foreground hover:text-foreground"
                  >
                    <RotateCcwIcon className="size-4" />
                    <span className="text-sm">Reset to Default</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <ResetDialog
        isOpen={isResetDialogOpen}
        onClose={() => setIsResetDialogOpen(false)}
        onConfirm={handleReset}
      />
    </>
  );
};

type ResetDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const ResetDialog = ({ isOpen, onClose, onConfirm }: ResetDialogProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[360px]">
        <DialogHeader>
          <DialogTitle>Reset layout</DialogTitle>
          <DialogDescription>
            This will restore the layout back to the default arrangement and
            show all panels. Any custom positioning will be lost.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button variant="destructive" onClick={onConfirm}>
            Reset Layout
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

